import { Link, useNavigate } from "@tanstack/react-router"
import type { ToolUIPart } from "ai"
import { ArrowRight, Loader2, SlidersHorizontal } from "lucide-react"
import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import { useCreateProcessRun } from "@/lib/queries"
import { useSessionContext } from "@/lib/session"
import { cn } from "@/lib/utils"
import {
  DIFFICULTY_LABELS,
  GESPRAECHSTRAINING_LABEL,
  TRAINING_ANLAESSE,
  TRAINING_PRODUKTE,
  einheitFuerPhase,
  type Difficulty,
  type GespraechsPhase,
} from "@nl/shared"

/** Mirror of the tool result in `apps/api/src/ai/training-suggestion-tools.ts`. */
type Suggestion =
  | {
      art: "pruefungsvorbereitung"
      lernfeldSlug?: string
      lernfeldLabel?: string
      begruendung: string
    }
  | {
      art: "gespraechstraining"
      phase: GespraechsPhase
      phaseLabel: string
      persona: {
        id: string
        name: string
        difficulty: Difficulty
        portraitUrl?: string
      }
      begruendung: string
    }
  | {
      art: "produkttraining"
      produkt: string
      produktLabel: string
      begruendung: string
    }
  | { art: "nicht-moeglich"; grund: string }

const THEMA_BILD =
  TRAINING_ANLAESSE.find((t) => t.key === "einkommensgutachten")?.imageUrl ??
  ""

/**
 * The coach's proposal as something to click, under his message.
 *
 * Starting is one click: the run is created here, in the advisor's browser,
 * from the persona and phase the tool resolved. The model never creates a
 * training, so nothing exists that nobody chose to start.
 */
export function TrainingSuggestionCard({ part }: { part: ToolUIPart }) {
  if (part.state === "input-streaming" || part.state === "input-available") {
    return (
      <div className="flex items-center gap-2 text-[13px] text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Training wird zusammengestellt …
      </div>
    )
  }
  if (part.state === "output-error") {
    return (
      <p className="text-[13px] text-muted-foreground">
        Der Vorschlag konnte nicht zusammengestellt werden.
      </p>
    )
  }

  const suggestion = part.output as Suggestion
  if (suggestion.art === "nicht-moeglich") {
    return <p className="text-[13px] text-muted-foreground">{suggestion.grund}</p>
  }
  switch (suggestion.art) {
    case "gespraechstraining":
      return <GespraechCard suggestion={suggestion} />
    case "produkttraining":
      return <ProduktCard suggestion={suggestion} />
    case "pruefungsvorbereitung":
      return <PruefungCard suggestion={suggestion} />
  }
}

/**
 * Der Weg in die Prüfungsvorbereitung aus dem offenen Chat.
 *
 * Bewusst ohne Bild: für die Prüfung gibt es keine Aufnahme, und ein
 * geliehenes Produktfoto würde etwas anderes versprechen, als dahinter liegt.
 * Wie bei den anderen beiden Karten schlägt der Coach nur vor — gestartet wird
 * mit dem Klick. Der Klick trägt den Bereich mit hinüber, damit drüben nicht
 * noch einmal nach dem Thema gefragt wird. Die Eröffnung selbst schickt er
 * nicht: vorher fragt der Coach nach dem Lernziel.
 */
function PruefungCard({
  suggestion,
}: {
  suggestion: Extract<Suggestion, { art: "pruefungsvorbereitung" }>
}) {
  return (
    <Frame
      eyebrow="Prüfungsvorbereitung"
      title={suggestion.lernfeldLabel ?? "IVFP-Prüfung"}
      line={suggestion.begruendung}
      actions={
        <Button asChild size="sm" className="gap-1.5">
          <Link
            to="/pruefung"
            search={
              suggestion.lernfeldSlug
                ? { lernfeld: suggestion.lernfeldSlug }
                : { gemischt: true }
            }
          >
            Abfrage starten
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </Button>
      }
    />
  )
}

function Frame({
  imageUrl,
  eyebrow,
  title,
  line,
  actions,
  imageClassName,
}: {
  /** Fehlt, wo es kein ehrliches Bild gibt. Die Karte rückt dann zusammen. */
  imageUrl?: string
  eyebrow: string
  title: string
  line: string
  actions: React.ReactNode
  imageClassName?: string
}) {
  return (
    <div className="flex overflow-hidden rounded-xl border border-[var(--rule)] bg-card shadow-[0_8px_24px_rgba(31,31,31,0.08)]">
      {imageUrl && (
        <img
          src={imageUrl}
          alt=""
          loading="lazy"
          className={cn(
            "hidden w-40 shrink-0 object-cover sm:block",
            imageClassName
          )}
        />
      )}
      <div className="min-w-0 flex-1 p-4">
        <div className="eyebrow">{eyebrow}</div>
        <div className="mt-1 font-display text-[16px] leading-snug font-medium tracking-tight">
          {title}
        </div>
        <p className="mt-1 text-[13px] leading-relaxed text-muted-foreground">
          {line}
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-2">{actions}</div>
      </div>
    </div>
  )
}

function GespraechCard({
  suggestion,
}: {
  suggestion: Extract<Suggestion, { art: "gespraechstraining" }>
}) {
  const navigate = useNavigate()
  const { user } = useSessionContext()
  const createRun = useCreateProcessRun()

  const start = () => {
    if (createRun.isPending) return
    createRun.mutate(
      {
        personaId: suggestion.persona.id,
        // The coach's suggestion names a phase; what gets trained is its unit.
        units: [einheitFuerPhase(suggestion.phase) ?? "rahmen_bedarf"],
        coachStyleId: user?.coachStyleId ?? undefined,
      },
      {
        onSuccess: (run) =>
          void navigate({ to: "/durchlauf/$runId", params: { runId: run.id } }),
        onError: () =>
          toast.error(
            `Das ${GESPRAECHSTRAINING_LABEL} konnte nicht gestartet werden.`
          ),
      }
    )
  }

  return (
    <Frame
      imageUrl={suggestion.persona.portraitUrl ?? THEMA_BILD}
      eyebrow={GESPRAECHSTRAINING_LABEL}
      title={`${suggestion.phaseLabel} mit ${suggestion.persona.name}`}
      line={`${suggestion.begruendung} (${DIFFICULTY_LABELS[suggestion.persona.difficulty]})`}
      actions={
        <>
          <Button
            type="button"
            size="sm"
            onClick={start}
            disabled={createRun.isPending}
            className="gap-1.5"
          >
            {createRun.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : null}
            Training starten
            <ArrowRight className="h-3.5 w-3.5" />
          </Button>
          <Button
            asChild
            type="button"
            size="sm"
            variant="ghost"
            className="gap-1.5 text-muted-foreground"
          >
            <Link
              to="/durchlauf"
              search={{
                schritt: "persona",
                phase: suggestion.phase,
                persona: suggestion.persona.id,
              }}
            >
              <SlidersHorizontal className="h-3.5 w-3.5" />
              Anpassen
            </Link>
          </Button>
        </>
      }
    />
  )
}

function ProduktCard({
  suggestion,
}: {
  suggestion: Extract<Suggestion, { art: "produkttraining" }>
}) {
  const produkt = TRAINING_PRODUKTE.find((p) => p.key === suggestion.produkt)
  return (
    <Frame
      imageUrl={produkt?.imageUrl ?? THEMA_BILD}
      eyebrow="Produkttraining"
      title={suggestion.produktLabel}
      line={suggestion.begruendung}
      actions={
        <Button asChild size="sm" className="gap-1.5">
          <Link to="/produkttraining" search={{ produkt: produkt?.key }}>
            Produkttraining starten
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </Button>
      }
    />
  )
}
